import {UserModel} from '../authentication/user.model';
import {StudentModel} from '../authentication/student.model';
import {ObjectiveModel} from '../edu-admin/objective.model';

export type MigrationStatus =
  | 'OBJECTIVE_CONFIRMED'
  | 'OBJECTIVE_ADVANCED'
  | 'MIGRATION_SKIPPED_NO_STUDENTS'
  | 'GROUP_CREATED_MID_YEAR'
  | 'NO_OBJECTIVE'
  | 'OBJECTIVE_MISMATCH_LOWER'
  | 'AT_MAX_OBJECTIVE'
  | 'MIXED_GROUP_NOT_ADVANCED'
  | 'NON_STANDARD_GROUP'
  | 'RESOLVED';

export const MIGRATION_STATUSES_NEEDING_ATTENTION: MigrationStatus[] = [
  'NO_OBJECTIVE',
  'OBJECTIVE_MISMATCH_LOWER',
  'AT_MAX_OBJECTIVE',
  'MIXED_GROUP_NOT_ADVANCED',
];

export const MIGRATION_STATUSES_NEEDING_REVIEW: MigrationStatus[] = [
  'OBJECTIVE_ADVANCED',
  'NON_STANDARD_GROUP',
];

export const GroupTypes = {
  standard: 'standard',
  mixed: 'mixed',
  specialEducation: 'specialEducation'
};

export class GroupModel {

  static currentGroupKey = 'currentGroupKey';
  static currentGroup: GroupModel = null;

  public _id: string;

  public name: string;
  public grade: number;
  public groupType: string;

  public teacherIds: string[];
  public studentIds: string[];

  schoolId: string;

  public objective: ObjectiveModel;
  public objectiveId: string;
  public schoolYearId: string;

  public migrationStatus: MigrationStatus | null;

  public students: StudentModel[] = [];
  public teachers: UserModel[] = [];

  public createdAt: Date;
  public updatedAt: Date;

  /**
   * Used for list showing.
   */
  public hasMoreData = false;

  /**
   * Used for list showing
   */
  public searchString: string = null;

  static generateModel(json: any): GroupModel {

    const group = new GroupModel();
    group._id = json._id;

    group.name = json.name;
    group.grade = json.grade;
    group.groupType = json.groupType;

    group.teacherIds = json.teacherIds;
    group.studentIds = json.studentIds;

    group.schoolId = json.schoolId;
    group.schoolYearId = json.schoolYearId;
    group.migrationStatus = json.migrationStatus != null ? json.migrationStatus : null;

    if (json.objectiveId && typeof json.objectiveId === 'object') {
      group.objective = ObjectiveModel.generateModel(json.objectiveId);
      group.objectiveId = group.objective._id;
    } else {
      group.objectiveId = json.objectiveId;
    }

    if (json.createdAt) {
      group.createdAt = new Date(json.createdAt);
    }

    if (json.updatedAt) {
      group.updatedAt = new Date(json.updatedAt);
    }

    return group;
  }

  static generateModels(jsonList: any[]): GroupModel[] {

    const list = [];

    for (const json of jsonList) {
      list.push(this.generateModel(json));
    }

    return list;

  }

  static getCurrent(): GroupModel {

    if (this.currentGroup) {
      return this.currentGroup;
    }

    const groupJson = localStorage.getItem(GroupModel.currentGroupKey);
    if (groupJson) {
      this.currentGroup = GroupModel.generateModel(JSON.parse(groupJson));
    }

    return this.currentGroup;
  }

  static setCurrent(group: GroupModel) {

    this.currentGroup = group;

    if (this.currentGroup) {
      localStorage.setItem(this.currentGroupKey, JSON.stringify(this.currentGroup));
    } else {
      this.removeCurrent();
    }

  }

  static removeCurrent() {
    this.currentGroup = null;
    localStorage.removeItem(this.currentGroupKey);
  }

  getTwoInitials(): string {

    if (!this.name) {
      return '';
    }

    return this.name.substring(0, 2).toUpperCase();

  }
}

export interface GroupActivitySummary {
  groupId: string;
  numAnswersLast7Days: number;
  numAnswersLast30Days: number;
  numAnswersThisYear: number;
  numActiveStudentsLast30Days: number;
  numStudents: number;
  lastAnswerDate: string | null;
}

export class GroupQueryModel {

  name: string;
  email: string = undefined;
  fromAge: number;
  toAge: number;
  isActive: undefined;
  skip = 0;
  limit: number = undefined;
  /**
   * Sorts by name
   */
  sort = -1;
}

/**
 * A summary model which gives us the grades associated with the groups in the
 * school
 */
export class GroupGradesSummary {

  gradeLevelsInUse: number[];
  hasGroupWithoutGradeLevel: boolean;
  groupsWithoutGradeLevelCount: number;

  static generate(json: any): GroupGradesSummary {

    const summary = new GroupGradesSummary();
    summary.gradeLevelsInUse = json.gradeLevelsInUse;
    summary.hasGroupWithoutGradeLevel = json.hasGroupWithoutGradeLevel;
    summary.groupsWithoutGradeLevelCount = json.groupsWithoutGradeLevelCount;

    return summary;

  }

}
